import type { AssetMonitoring, MonitorStatusCode } from '@ftthnms/shared';
import type { MonitorStatus } from '@ftthnms/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export const useAssetMonitoring = (id: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: ['asset', id, 'monitoring'],
    queryFn: () => api<AssetMonitoring>(`/api/v1/monitoring/assets/${id}`),
    enabled: !!id && enabled,
  });

/** Design system StatusIndicator states match the monitoring statuses one to one. */
export const toIndicator = (s: MonitorStatusCode | string | null | undefined): MonitorStatus =>
  (['online', 'warning', 'offline', 'maintenance'].includes(s ?? '') ? s : 'unknown') as MonitorStatus;

const rtf = new Intl.RelativeTimeFormat('id-ID', { numeric: 'auto' });

/** "3 menit yang lalu". */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = Math.round((Date.parse(iso) - Date.now()) / 1000);
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, 'second');
  if (abs < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
}

/** "3 hari 4 jam". */
export function uptime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} hari ${h} jam`;
  if (h > 0) return `${h} jam ${m} mnt`;
  return `${m} mnt`;
}

/** Web UI of a device (MON-13). */
export function webUiUrl(ip: string, httpsPort: number | null, httpPort: number | null): string | null {
  if (httpsPort) return `https://${ip}${httpsPort === 443 ? '' : `:${httpsPort}`}`;
  if (httpPort) return `http://${ip}${httpPort === 80 ? '' : `:${httpPort}`}`;
  return null;
}

/** Asset types that carry an IP address and can be monitored. */
export const MONITORABLE_TYPES = ['OLT', 'SERVER', 'AP', 'CPE'];
